import { Component, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogContent
} from "@angular/material/dialog";
import { STEPPER_GLOBAL_OPTIONS } from "@angular/cdk/stepper";
import {
  MatStepper,
  MatStep,
  MatStepperNext,
  MatStepperPrevious
} from "@angular/material/stepper";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatRadioModule } from "@angular/material/radio";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatCard, MatCardContent } from "@angular/material/card";
import { MatTableModule } from "@angular/material/table";
import { MatSnackBar } from "@angular/material/snack-bar";
import { DialogHeaderComponent } from "@app/view/shared/dialog-header/dialog-header.component";
import { DataImportService } from "@app/services/data-import/data-import.service";
import { ExcelService } from "@app/services/excel/excel.service";
import {
  ImportEntityType,
  ImportValidationResponse,
  ResourceFullImportData,
  ResourceImportRecord,
  SorCodeImportRecord,
  ValidationError
} from "@app/models/dto/data-import.model";

/**
 * Import Wizard Dialog Component
 * Provides a step-by-step wizard for importing Resources or SOR Codes from Excel files.
 * CR: ROCC-CR008745
 */
@Component({
  selector: "app-data-import-wizard",
  templateUrl: "./data-import-wizard.component.html",
  styleUrls: ["./data-import-wizard.component.scss"],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { showError: true }
    }
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogContent,
    MatStepper,
    MatStep,
    MatStepperNext,
    MatStepperPrevious,
    MatButton,
    MatIconButton,
    MatRadioModule,
    MatIcon,
    MatProgressSpinner,
    MatCard,
    MatCardContent,
    MatTableModule,
    DialogHeaderComponent
  ]
})
export class DataImportWizardComponent {
  dialogRef = inject<MatDialogRef<DataImportWizardComponent>>(MatDialogRef);
  data = inject<{
    entityType?: ImportEntityType;
  }>(MAT_DIALOG_DATA);
  private dataImportService = inject(DataImportService);
  private excelService = inject(ExcelService);
  private snackBar = inject(MatSnackBar);

  // Wizard state
  entityType: ImportEntityType = "RESOURCE";
  hasPresetEntityType = false;
  selectedFile: File | null = null;
  parsedRecords: ResourceImportRecord[] | SorCodeImportRecord[] = [];
  parsedFullImportData: ResourceFullImportData | null = null;
  validationResponse: ImportValidationResponse | null = null;

  // Loading states
  isDownloading = false;
  isParsing = false;
  isValidating = false;
  isCommitting = false;

  // Results
  commitSuccess = false;
  commitMessage = "";

  // Error table columns
  errorDisplayedColumns: string[] = ["rowIndex", "field", "message", "value"];

  constructor() {
    const data = this.data;

    // Set initial entity type from dialog data if provided
    if (data?.entityType) {
      this.entityType = data.entityType;
      this.hasPresetEntityType = true;
    }
  }

  // ============================================
  // STEP 1: SELECT & DOWNLOAD
  // ============================================

  async downloadTemplate(withData: boolean): Promise<void> {
    this.isDownloading = true;
    try {
      if (this.entityType === "RESOURCE") {
        if (withData) {
          await this.dataImportService.exportResourcesToExcel();
        } else {
          await this.dataImportService.downloadResourceTemplate();
        }
      } else {
        if (withData) {
          await this.dataImportService.exportSorCodesToExcel();
        } else {
          await this.dataImportService.downloadSorCodeTemplate();
        }
      }
      this.snackBar.open("Template downloaded successfully", "Close", {
        duration: 3000
      });
    } catch (error) {
      console.error("Download error:", error);
      this.snackBar.open("Failed to download template", "Close", {
        duration: 5000
      });
    } finally {
      this.isDownloading = false;
    }
  }

  // ============================================
  // STEP 2: UPLOAD FILE
  // ============================================

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.validationResponse = null; // Reset validation when new file selected
    }
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      if (this.excelService.isValidExcelFile(file)) {
        this.selectedFile = file;
        this.validationResponse = null;
      } else {
        this.snackBar.open(
          "Invalid file type. Please select an Excel file (.xlsx)",
          "Close",
          { duration: 5000 }
        );
      }
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  clearFile(): void {
    this.selectedFile = null;
    this.parsedRecords = [];
    this.parsedFullImportData = null;
    this.validationResponse = null;
  }

  async parseAndValidate(stepper: MatStepper): Promise<void> {
    if (!this.selectedFile) {
      return;
    }

    this.isParsing = true;
    try {
      // For RESOURCE type, use full parsing to include child records
      if (this.entityType === "RESOURCE") {
        this.parsedFullImportData =
          await this.excelService.parseResourceExcelFull(this.selectedFile);
        this.parsedRecords = this.parsedFullImportData.resources;
      } else {
        // For SOR_CODE, use simple parsing
        this.parsedRecords = await this.dataImportService.parseFile(
          this.selectedFile,
          this.entityType
        );
      }

      if (this.parsedRecords.length === 0) {
        this.snackBar.open(
          "No data rows found in the file. Please check the file format.",
          "Close",
          { duration: 5000 }
        );
        return;
      }

      this.isParsing = false;
      this.isValidating = true;

      // Validate with backend (including child records for resources)
      if (this.entityType === "RESOURCE" && this.parsedFullImportData) {
        this.validationResponse =
          await this.dataImportService.validateResourceFull(
            this.parsedFullImportData
          );
      } else {
        this.validationResponse = await this.dataImportService.validate(
          this.parsedRecords,
          this.entityType
        );
      }

      // Move to next step
      stepper.next();
    } catch (error: unknown) {
      console.error("Parse/validate error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to process file";
      this.snackBar.open(errorMessage, "Close", { duration: 5000 });
    } finally {
      this.isParsing = false;
      this.isValidating = false;
    }
  }

  // ============================================
  // STEP 3: REVIEW & COMMIT
  // ============================================

  async commitImport(stepper: MatStepper): Promise<void> {
    if (!this.validationResponse?.valid) {
      this.snackBar.open(
        "Cannot commit. Please fix validation errors first.",
        "Close",
        { duration: 5000 }
      );
      return;
    }

    this.isCommitting = true;
    try {
      let response;

      // For RESOURCE type with full import data, use the dedicated method
      if (this.entityType === "RESOURCE" && this.parsedFullImportData) {
        response = await this.dataImportService.commitResourceFull(
          this.parsedFullImportData
        );
      } else {
        response = await this.dataImportService.commit(
          this.parsedRecords,
          this.entityType
        );
      }

      if (response.success) {
        this.commitSuccess = true;
        this.commitMessage = response.message;
        stepper.next();
      } else {
        this.snackBar.open(response.message, "Close", { duration: 5000 });
      }
    } catch (error: unknown) {
      console.error("Commit error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to commit import";
      this.snackBar.open(errorMessage, "Close", { duration: 5000 });
    } finally {
      this.isCommitting = false;
    }
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  getEntityTypeLabel(): string {
    return this.entityType === "RESOURCE" ? "Resources" : "SOR Codes";
  }

  isWithinCoreHours(): boolean {
    return this.dataImportService.isWithinCoreHours();
  }

  getErrorsForDisplay(): ValidationError[] {
    return this.validationResponse?.errors || [];
  }

  close(): void {
    this.dialogRef.close(this.commitSuccess);
  }
}
